import type { RowSelectionStore } from "@/components/data-table/context/RowSelectionContext";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useCallback, useRef, useSyncExternalStore } from "react";

const EMPTY_ROW_SELECTION: RowSelectionState = {};

function selectionToRowSelection(sel: ReadonlySet<string>): RowSelectionState {
  if (sel.size === 0) {
    return EMPTY_ROW_SELECTION;
  }
  const rowSelectionState: RowSelectionState = {};
  for (const id of sel) {
    rowSelectionState[String(id)] = true;
  }
  return rowSelectionState;
}

function applyRowSelectionToStore(
  store: RowSelectionStore,
  prev: RowSelectionState,
  next: RowSelectionState,
) {
  const prevKeys = new Set(
    Object.entries(prev)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => id),
  );
  const nextKeys = new Set(
    Object.entries(next)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => id),
  );
  for (const id of prevKeys) {
    if (!nextKeys.has(id) && store.isSelected(id)) {
      store.toggle(id);
    }
  }
  for (const id of nextKeys) {
    if (!prevKeys.has(id) && !store.isSelected(id)) {
      store.toggle(id);
    }
  }
}

export function useSyncedRowSelectionFromStore(store: RowSelectionStore) {
  const snapshotCacheRef = useRef<{
    serialized: string;
    state: RowSelectionState;
  }>({
    serialized: "",
    state: EMPTY_ROW_SELECTION,
  });

  const rowSelection = useSyncExternalStore(
    store.subscribe,
    () => {
      const serialized = [...store.getSelection()].sort().join("\u0000");
      if (snapshotCacheRef.current.serialized === serialized) {
        return snapshotCacheRef.current.state;
      }
      const state = selectionToRowSelection(store.getSelection());
      snapshotCacheRef.current = { serialized, state };
      return state;
    },
    () => EMPTY_ROW_SELECTION,
  );

  const setRowSelection = useCallback<OnChangeFn<RowSelectionState>>(
    (updater) => {
      const prev = selectionToRowSelection(store.getSelection());
      const next = typeof updater === "function" ? updater(prev) : updater;
      applyRowSelectionToStore(store, prev, next);
    },
    [store],
  );

  return { rowSelection, setRowSelection };
}
